import React from 'react';
import { FormState } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { CreateJobDto } from '../../../../../../services/applicant_tracking/JobService/CreateJobDto';
import {
  SetupHeaderButtonType,
  SetupPageHeader,
} from '../../../../../../components/SetupPageHeader';
import { replaceFinalSegment } from '../../../../../../utils/url';
import { useStateMachine } from 'little-state-machine';
import { JobDetails } from '../../../../../../services/applicant_tracking/JobService/EditJobDto';
import { SetupPageFooter } from '../../../../../../components/SetupPageFooter';

interface PropTypes {
  setupStep: number;
  formState: FormState<CreateJobDto>;
  isEditFlow: boolean;
  isLoading: boolean;
  isDeleteModalOpen?: boolean;
  jobDetails: JobDetails;
  setSetupStep: (newStep: number, jobDTO: CreateJobDto) => void;
  onCancel: () => void;
  onSave: () => void;
  onSubmit: () => void;
  onNavigate: () => void;
  children: React.ReactNode;
}

export function JobWorkflowLayout(props: PropTypes) {
  const isLoadingOrFormInvalid = props.isLoading || !props.formState.isValid;
  const isDisabled = isLoadingOrFormInvalid || props.isDeleteModalOpen;
  const navigate = useNavigate();
  const { state } = useStateMachine();

  const firstItems: SetupHeaderButtonType[] = [
    {
      title: 'Previous',
      type: 'Navigate',
      action: () => {
        props.onNavigate();
        props.setSetupStep(props.setupStep - 1, state.setupJob);
        navigate(replaceFinalSegment('application_form'));
      },
      disabled: isDisabled,
    },
  ];

  const saveAsDraft: SetupHeaderButtonType = {
    title: 'Save as Draft',
    type: 'Save',
    action: props.onSave,
    disabled: isDisabled,
  };

  const nextButton: SetupHeaderButtonType = {
    title: 'Next',
    type: 'Navigate',
    action: () => {
      props.onNavigate();
      props.setSetupStep(props.setupStep + 1, state.setupJob);
      navigate(replaceFinalSegment('free_job_boards'));
    },
    disabled: isDisabled,
  };

  const saveButtonText =
    props.isEditFlow && props.jobDetails?.status === 'published'
      ? 'Save and Publish'
      : 'Save and Continue';

  const saveAndContinue: SetupHeaderButtonType = {
    title: saveButtonText,
    type: 'Save',
    disabled: isDisabled,
    action: props.onSubmit,
  };

  if (props.isEditFlow) {
    firstItems.push(nextButton);
  }

  return (
    <>
      <SetupPageHeader
        title='Job Workflow'
        firstItems={firstItems}
        intermediateItems={[
          {
            title: 'Cancel',
            type: 'Cancel',
            action: props.onCancel,
            disabled: props.isDeleteModalOpen,
          },
          saveAndContinue,
        ]}
      />
      {props.children}
      {!props.isLoading && (
        <SetupPageFooter
          classNames='mt-4'
          firstItem={saveAndContinue}
          lastItem={props.isEditFlow ? null : saveAsDraft}
        />
      )}
    </>
  );
}
